import React from 'react';
import { Armchair } from 'lucide-react';

/**
 * Seat assignments are stored as free-text strings entered by admins,
 * e.g. "8A WINDOW", "EXIT 11A WINDOW", or just "12C". Parse out the seat
 * number so it can be displayed compactly, with any remaining descriptors
 * (Window / Middle / Aisle / Exit) preserved for tooltips.
 */
export function parseSeatLabel(raw) {
  const text = String(raw || '').trim();
  if (!text) return null;

  const tokens = text.split(/\s+/);
  const seatIdx = tokens.findIndex((t) => /^\d{1,3}[A-Z]{1,2}$/i.test(t));

  if (seatIdx === -1) {
    return { seat: text, label: '' };
  }

  const descriptors = tokens
    .filter((_, i) => i !== seatIdx)
    .map((t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());

  return {
    seat: tokens[seatIdx].toUpperCase(),
    label: descriptors.join(' · '),
  };
}

/**
 * Renders seat assignments for one flight segment as an inline text list
 * including any descriptors, e.g. "5A (Window) · 5B (Middle)".
 */
export default function SegmentSeatChips({ seats, className = '' }) {
  if (!seats || seats.length === 0) return null;

  const parsed = seats.map(parseSeatLabel).filter(Boolean);
  if (parsed.length === 0) return null;

  const display = parsed
    .map((p) => (p.label ? `${p.seat} (${p.label})` : p.seat))
    .join(' · ');

  return (
    <div
      className={`flex items-center gap-1.5 min-w-0 ${className}`}
      title={display}
    >
      <Armchair className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
      <span className="text-white/90 font-medium break-words">{display}</span>
    </div>
  );
}
